import ChecklistRtlOutlinedIcon from '@mui/icons-material/ChecklistRtlOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import {
  CLONE_META_DETAIL_TEXT_SX,
  TableMetaInlineRow,
  TableMetaStack,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { resolveTableMetricAccentColor } from '@/features/tests/components/testsTableShared';
import { s, typographyTokens } from '@/theme/tokens';
import { Box } from '@/ui';
import type { ReactElement } from 'react';

type MetricTone = 'neutral' | 'green' | 'red' | 'orange' | 'brown' | 'blue' | 'purple' | 'teal';
type MetricDirection = 'higherBetter' | 'lowerBetter';
type MetricKey = 'passRate' | 'frequency' | 'avgDuration';

type TableMetricsBreakdownItem = {
  key: MetricKey;
  label: string;
  valueLabel: string;
  deltaValue: number | null;
  deltaLabel?: string | null;
  isMissing?: boolean;
  tone: MetricTone;
  direction: MetricDirection;
};

type TableMetricsBreakdownCellProps = {
  items: [TableMetricsBreakdownItem, TableMetricsBreakdownItem, TableMetricsBreakdownItem];
};

const metricDeltaTextSx = {
  color: 'text.secondary',
  fontSize: typographyTokens.captionSmall.fontSize,
  display: 'inline-flex',
  alignItems: 'center',
  gap: s('xs'),
} as const;

const renderMetricTrendIcon = (
  deltaLabel: string | null | undefined,
  rawDeltaDirection: MetricDirection,
  rawDeltaValue: number | null,
): ReactElement | null => {
  if (!deltaLabel || rawDeltaValue === null || rawDeltaValue === 0) return null;
  const isImproving = rawDeltaDirection === 'higherBetter' ? rawDeltaValue > 0 : rawDeltaValue < 0;

  return (
    <Box
      component="span"
      sx={{
        fontSize: (theme) => theme.typography.caption.fontSize,
        color: isImproving ? 'success.main' : 'error.main',
        lineHeight: 1,
      }}
    >
      {isImproving ? '▲' : '▼'}
    </Box>
  );
};

const resolveMetricIcon = (key: MetricKey): ReactElement => {
  if (key === 'passRate') {
    return (
      <ChecklistRtlOutlinedIcon
        sx={{
          fontSize: (theme) => theme.app.iconSizes.sm,
          transform: 'translateY(1px)',
        }}
      />
    );
  }
  if (key === 'frequency') {
    return <ReplayOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />;
  }
  return <TimerOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />;
};

export const TableMetricsBreakdownCell = ({
  items,
}: TableMetricsBreakdownCellProps): ReactElement => {
  return (
    <TableMetaStack rowGap={s('sm')}>
      {items.map((item) => (
        <TableMetaInlineRow
          key={item.key}
          icon={resolveMetricIcon(item.key)}
          textSx={CLONE_META_DETAIL_TEXT_SX}
          content={
            <>
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                {item.label}:{' '}
              </Box>
              <Box
                component="span"
                sx={{
                  color: item.isMissing
                    ? 'text.secondary'
                    : (theme) => resolveTableMetricAccentColor(theme, item.tone),
                  whiteSpace: 'nowrap',
                }}
              >
                {item.valueLabel}
              </Box>
              {item.deltaLabel ? (
                <Box
                  component="span"
                  sx={[
                    metricDeltaTextSx,
                    {
                      ml: s('sm'),
                      whiteSpace: 'nowrap',
                    },
                  ]}
                >
                  {item.deltaLabel}
                  {renderMetricTrendIcon(item.deltaLabel, item.direction, item.deltaValue)}
                </Box>
              ) : null}
            </>
          }
        />
      ))}
    </TableMetaStack>
  );
};
